export const useUserStore = defineStore('user', () => {
  const api = useApi()
  const billingStore = useBillingStore()
  const teamsStore = useTeamsStore()

  const user = ref<User>()
  const userGeolocation = ref<UserGeolocation>()
  const userOnboarding = ref<UserOnboarding>()
  const userSettings = ref<UserSettings>()
  const userTeamInvitations = ref<TeamInvitation[]>([])

  const userType = computed(() => {
    if (billingStore.isSubscriptionActive || teamsStore.currentTeam?.admin) {
      return 'subscriber'
    }
    else {
      return 'user'
    }
  })

  const fetchUser = async () => {
    try {
      const { data: userFetched } = await api.user.fetch()

      if (userFetched) {
        setUser({ user: userFetched })

        await fetchUserDependencies()
      }
    }
    catch (error) {
      return Promise.reject(error)
    }

    return user.value
  }

  const fetchUserDependencies = async () => {
    try {
      if (user.value) {
        await fetchUserOnboarding()
        await fetchUserSettings()
        await teamsStore.fetchTeams()

        if (user.value.currentTeamId) {
          teamsStore.currentTeamId = user.value.currentTeamId

          await teamsStore.fetchTeamDependencies()
        }
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchUserGeolocation = async () => {
    try {
      const continent = localStorage.getItem('continent')

      if (!continent) {
        const { data: geolocation } = await api.user.geolocation.fetch()

        userGeolocation.value = geolocation
      }

      if (import.meta.client && userGeolocation.value) {
        localStorage.setItem('continent', userGeolocation.value.continent)
        useCookie('continent').value = userGeolocation.value.continent
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchUserOnboarding = async () => {
    try {
      const { data: userOnboardingFetched } = await api.user.onboarding.fetch()

      userOnboarding.value = userOnboardingFetched
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchUserSettings = async () => {
    try {
      const { data: userSettingsFetched } = await api.user.settings.fetch()

      userSettings.value = userSettingsFetched
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchUserTeamInvitations = async () => {
    try {
      const { data: teamInvitations } = await api.user.invitations.fetch()

      userTeamInvitations.value = teamInvitations
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const processUserTeamInvitation = async (params: {
    invitationToken: string
  }) => {
    try {
      const { data: team } = await api.user.invitations.process(params.invitationToken)

      // Refresh teams
      await teamsStore.fetchTeams()

      return team
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const updateUser = async (params: {
    colorMode?: 'dark' | 'light' | 'system'
    email?: string
    fullName?: string
    locale?: Locale
    referral?: string
  }) => {
    try {
      const { data: userFetched } = await api.user.update(params)

      setUser({ user: userFetched })
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const updateUserPassword = async (params: {
    currentPassword?: string
    newPassword: string
    newPasswordConfirmation: string
  }) => {
    try {
      const { data: userFetched } = await api.user.updatePassword({
        currentPassword: params.currentPassword,
        newPassword: params.newPassword,
        newPasswordConfirmation: params.newPasswordConfirmation,
      })

      setUser({ user: userFetched })
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const updateUserSettings = async (params: {
    companySections: UserSettingsCompanySection[]
  }) => {
    try {
      const { data: userSettingsFetched } = await api.user.settings.update(params)

      userSettings.value = userSettingsFetched
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  // Setters
  const resetStore = () => {
    user.value = undefined
    userGeolocation.value = undefined
    userOnboarding.value = undefined
    userSettings.value = undefined
    userTeamInvitations.value = []
  }

  const setUser = (params: {
    user: User
  }) => {
    user.value = params.user

    // Initialize services
    if (import.meta.client) {
      localStorage.setItem('user', user.value ? JSON.stringify(user.value) : '')

      // Crisp
      if (window.$crisp) {
        window.$crisp.push([
          'set',
          'user:email',
          user.value.email,
        ])
      }
    }
  }

  return {
    fetchUser,
    fetchUserDependencies,
    fetchUserGeolocation,
    fetchUserOnboarding,
    fetchUserSettings,
    fetchUserTeamInvitations,
    processUserTeamInvitation,
    resetStore,
    setUser,
    updateUser,
    updateUserPassword,
    updateUserSettings,
    user,
    userGeolocation,
    userOnboarding,
    userSettings,
    userTeamInvitations,
    userType,
  }
})
